import React, { useEffect, useState, useRef, Suspense, lazy } from 'react'
import type { ChatbotData } from '../../models/types'
import { useRetorikStore } from '../Contexts/RetorikContext'
import { useSpeechStore } from '../Contexts/SpeechContext'
import { useUtilsStore } from '../Contexts/UtilsContext'
import { UIPart } from '../../models/enums'
import Subtitle from './Subtitle'

const ThreeJsWrapper = lazy(() => import('./ThreeJsWrapper'))
const SpiritEngineWrapper = lazy(() => import('./SpiritEngineWrapper'))

interface ChatbotAndSubtitlesContainerProps {
  centered?: boolean
  chatbotData?: ChatbotData
  showSubtitles?: boolean
  isNewsView?: boolean
  visible: boolean
}

const intervalInMilliseconds = 100

const ChatbotAndSubtitlesContainer = ({ centered, chatbotData, showSubtitles, isNewsView, visible }: ChatbotAndSubtitlesContainerProps): JSX.Element => {
  const agentData = useRetorikStore((state) => state.agentData)
  const speaking = useSpeechStore((state) => state.speaking)
  const boundaryData = useSpeechStore((state) => state.boundaryData)
  const setClickedOnUI = useUtilsStore((state) => state.actions.setClickedOnUI)
  const [speechIntervals, setSpeechIntervals] = useState<Array<number>>([])
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false)
  const [elapsedTime, setElapsedTime] = useState<number>(0)
  const timerRef = useRef<any>(null)

  const [gridRowStart, setGridRowStart] = useState<number>()
  const [gridRowSpan, setGridRowSpan] = useState<number>()

  useEffect(() => {
    if (chatbotData?.containerRowStart && chatbotData.containerRowStart > 0 && chatbotData.containerRowStart < 13) {
      if (chatbotData.containerRowEnd && chatbotData.containerRowEnd < 13) {
        if (chatbotData.containerRowStart < chatbotData.containerRowEnd) {
          setGridRowStart(chatbotData.containerRowStart)
          setGridRowSpan(chatbotData.containerRowEnd - chatbotData.containerRowStart)
        }
      } else {
        setGridRowStart(chatbotData.containerRowStart)
        setGridRowSpan(13 - chatbotData.containerRowStart)
      }
    } else if (chatbotData?.containerRowEnd && chatbotData.containerRowEnd > 0 && chatbotData.containerRowEnd < 13) {
      setGridRowStart(1)
      setGridRowSpan(chatbotData.containerRowEnd - 1)
    } else {
      setGridRowStart(1)
      setGridRowSpan(12)
    }
  }, [chatbotData])

  useEffect(() => {
    if (isNewsView) {
      setIsSpeaking(speaking)
    } else if (speaking && timerRef) {
      timerRef.current && clearInterval(timerRef.current)
      const now = Date.now()
      timerRef.current = setInterval(() => {
        setElapsedTime(Date.now() - now)
      }, intervalInMilliseconds)
    } else {
      timerRef?.current && clearInterval(timerRef.current)
      setElapsedTime(0)
    }

    return () => {
      timerRef?.current && clearInterval(timerRef.current)
    }
  }, [speaking])

  useEffect(() => {
    if (boundaryData.length > 0) {
      const tempArray: Array<number> = []
      // While animations can't keep up, let's just synchronize the beginning and the end of speech
      const firstWord = boundaryData.find((boundary) => boundary.boundaryType === 'WordBoundary')
      const lastWord = boundaryData
        .slice()
        .reverse()
        .find((boundary) => boundary.boundaryType === 'WordBoundary')

      if (firstWord && lastWord) {
        tempArray.push(firstWord.startTime / 10000)
        tempArray.push(lastWord.endTime / 10000)
      }

      setSpeechIntervals(tempArray)
    } else {
      setSpeechIntervals([])
    }
  }, [boundaryData])

  useEffect(() => {
    if (elapsedTime && speechIntervals.length > 0) {
      let inside = false
      for (let i = 0; i < speechIntervals.length; i++) {
        if (speechIntervals[i] > elapsedTime) {
          inside = true
          setIsSpeaking(!(i % 2 === 0))
          break
        }
      }

      !inside && setIsSpeaking(false)
    } else {
      setIsSpeaking(false)
    }
  }, [elapsedTime])

  const handleClick = (e: React.MouseEvent): void => {
    setClickedOnUI(UIPart.agent)
    e.stopPropagation()
    e.preventDefault()
  }

  return agentData ? (
    <React.Fragment>
      <div
        id='retorik-animation-container'
        className={`rf-relative rf-z-agent rf-col-start-1 rf-col-end-9 ${!centered && 'landscape-retorik:-rf-translate-x-1/4'} rf-row-span-full rf-self-end rf-h-full`}
        style={{
          visibility: visible ? 'visible' : 'hidden',
          gridRow: `${gridRowStart} / span ${gridRowSpan}`
        }}
        onClick={(e) => handleClick(e)}
      >
        <Suspense>
          {agentData.useBodyEngineThree ? (
            <ThreeJsWrapper agentData={agentData} visible={visible} height={chatbotData?.height} size={chatbotData?.size} cameraPreset={chatbotData?.cameraPreset} />
          ) : (
            <SpiritEngineWrapper agentData={agentData} height={chatbotData?.height} size={chatbotData?.size} isSpeaking={isSpeaking} />
          )}
        </Suspense>
      </div>
      {showSubtitles && <Subtitle isSpeaking={isSpeaking} elapsedTime={elapsedTime} />}
    </React.Fragment>
  ) : (
    <React.Fragment />
  )
}

export default ChatbotAndSubtitlesContainer
